import { get } from "lodash";
import { axiosChannels, axiosAgentService } from "../../configs/http-config";
import { setOrigin, setWidgetToken } from "../../utils/widgetStorage";
import { SessionManager } from "../../session/SessionManager";

/**
 * Fetches widget config and assistant, updates context and storage.
 * @param {string} botId
 * @param {string} tenantId
 * @param {string} lang
 * @param {string} assistantId
 * @param {string} UUID
 * @param {MessageEvent} event - verifyOrigin event (event.origin, event.data.href)
 * @param {Object} callbacks
 * @param {function(string)} callbacks.setPathName
 * @param {function(string)} callbacks.setHostName
 * @param {function(*)} callbacks.setAssistant
 * @param {function(*)} callbacks.setWidget
 * @param {function(boolean)} callbacks.setLoading
 * @param {function(Array)} callbacks.onDomainsLoaded - called with widgetData.domains when loaded
 */
export async function fetchWidget(botId, tenantId, lang, assistantId, UUID, event, callbacks) {
	const { setPathName, setHostName, setAssistant, setWidget, setLoading, onDomainsLoaded } = callbacks;

	try {
		const origin = event.origin;
		const hrefPath = event.data.href ? new URL(event.data.href).pathname : "";

		setPathName(hrefPath);
		setHostName(origin);

		const headers = {
			"X-Host": origin,
			"X-Pathname": hrefPath
		};

		setOrigin({ hostname: origin, pathname: hrefPath });

		// The bootstrap is served by unified-channel-service (it moved with the `widgets` collection
		// at the channel cutover and is the widget-token ISSUER); chat has no /widgets route at all
		// since Phase-4 CD1. axiosChannels also presents the prior token so a returning visitor
		// keeps its identity.
		const response = await axiosChannels.get(`/widgets/bootstrap/${botId}`, {
			params: { tenantId, lang },
			headers
		});

		let widgetData = null;
		if (response.status === 200) {
			widgetData = get(response, "data.data", null);
			if (widgetData) {
				// Adopt the server-minted identity when present. Inert (zero
				// behaviour change) until WIDGET_TOKEN_SECRET is set server-side
				// and the bootstrap response carries a token.
				if (widgetData.widgetToken) {
					setWidgetToken(widgetData.widgetToken);
					if (widgetData.contactId) {
						SessionManager.adoptServerContactId(widgetData.contactId);
					}
				}
				setWidget(widgetData);
				if (Array.isArray(widgetData.domains) && onDomainsLoaded) {
					onDomainsLoaded(widgetData.domains);
				}
			} else {
				console.warn("No widget data found in response.");
			}
		} else {
			console.error(`Failed to fetch widget. Status code: ${response.status}`);
		}

		// When the bot is connected to a swarm, the embed-script `assistantId`
		// is the swarm `_id`. UCS resolves the primary member and returns
		// `primaryAssistantId` so we fetch the real assistant config here.
		const effectiveAssistantId = widgetData?.primaryAssistantId || assistantId;
		const { data: responseAssistant } = await axiosAgentService.get(`/assistants/messenger/${effectiveAssistantId}`, {
			params: { tenantId, uuid: UUID, botId },
			headers
		});
		setAssistant(responseAssistant.data);
	} catch (error) {
		console.error("Error fetching widget:", error);
	} finally {
		setLoading(false);
	}
}
